import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { of, from, Subscription, Subject, Observable, tap, last } from 'rxjs';
import { map, switchMap, concatMap, filter, debounceTime, distinctUntilChanged, finalize } from 'rxjs/operators';
import { MessageService } from 'primeng/api';
import { cloneDeep } from 'lodash';

import { environment } from 'src/environments/environment';
import { round1, round2 } from 'src/app/shared/round';

import { Document, DocItems, DocumentStatusFlag } from 'src/app/shared/document';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { ProductApiService, StockResult } from 'src/app/services/product-api.service';
import { ClientApiService } from 'src/app/services/client-api.service';
import { RestApiService } from 'src/app/services/rest-api.service';
import { Product } from '../../shared/product';

/*
  Automated sequence to create documents to move items to production site and return completed products back
  ================================================================================================
  The component listens for a command input to start processing document items.
  It uses a series of RxJS Subjects and Subscriptions to manage the flow of operations.
  Each subscription handles a specific step in the process, passing data along to the next step.
  The process includes preparing document templates, checking stock availability, creating movement documents,
  and optionally creating return documents for completed products.
  The component emits the final set of created documents via an output event.
  ================================================================================================

  KNOWN ISSUE - not yet fixed, does not block current use (owner, 2026-09-02):

  Inconsistent stock-check date basis. Several stock/shipped-quantity checks pass `new Date()`
  ("as of right now") - createNewDocumentProcessSubscription's getProductShippedByDoc and
  getProdInfoById calls, a2MoveOptions$'s getProductStockBefore call - while others pass the
  document's own date (doc.DOCDATE in createNewManDocumentSubscription's
  getProductPartStockBefore/getProdIncomePriceById calls, this.newDocument.DOCDATE in
  a2MoveOptions$'s getProdInfoById call for parts) for what is conceptually the same "as-of"
  question: how much of this item/part is on stock, already shipped, or already moved to
  production, at the moment this check matters. Which basis is correct is a business-rule
  question (does a back-dated production run check stock as of right now, or as of its own
  date?) that hasn't been decided - see docs/technical-backlog.md B-28. Do not "fix" this by
  guessing; get the answer first.
*/

/*
sequenceDiagram
    participant User as User/Parent Component
    participant Comp as NewDocumentDlgProcessTranProdComponent
    participant newDocumentProcess$
    participant a2MoveOptions$
    participant createNewManDocument$
    participant createNewBackDocument$

    User->>Comp: Sets @Input() command = 1
    Comp->>Comp: processDocItems()
    Comp->>newDocumentProcess$: .next(currencyId)
    newDocumentProcess$->>Comp: Triggers createNewDocumentProcessSubscription
    Note right of Comp: 
      1. Prepares document template
      2. Checks shipped items
      3. Gathers product info
    Comp->>a2MoveOptions$: .next(moveOptions) in finalize()
    
    a2MoveOptions$->>Comp: Triggers createA2MoveSubscription
    Note right of Comp: 
      4. Iterates through items to produce
      5. Creates document prototypes for each stock
    loop For each item & stock
        Comp->>createNewManDocument$: .next(docPrototype)
    end
    
    createNewManDocument$->>Comp: Triggers createNewManDocumentSubscription
    Note right of Comp: 
      6. Fills doc details
      7. Rolls out parts
      8. Checks stock & prices
      9. Cleans up unneeded items
      
    alt moveProductionBack is true
        Comp->>createNewBackDocument$: .next(backDoc) in finalize()
    end
    
    createNewBackDocument$->>Comp: Triggers createNewBackSubscription
    Note right of Comp: 
      10. Calculates return price
      11. Finalizes the "return" document
*/

/*
Key Subscriptions
  1.	createNewDocumentProcessSubscription
    •	Trigger: newDocumentProcess$.next() is called from processDocItems() when the @Input() command is set to 1.
    •	Purpose: This is the entry point. It prepares a template newDocument by fetching currency rates,
      filtering items, and performing pre-flight checks (e.g., checking for already shipped quantities).
    •	Chain: On completion (finalize), it triggers the next step by calling a2MoveOptions$.next().

  2.	createA2MoveSubscription
    •	Trigger: a2MoveOptions$.next() is called from the finalize block of the previous subscription.
    •	Purpose: It generates one or more document prototypes for moving components from various stocks
      (main, import, auxiliary) to the manufacturing site.
    •	Chain: For each manufactured item and selected stock, it calls createNewManDocument$.next(),
      effectively queuing up multiple document creation tasks.

  3.	createNewManDocumentSubscription
    •	Trigger: createNewManDocument$.next() is called multiple times by the createA2MoveSubscription.
    •	Purpose: This is the core logic for creating the "move to production" documents. It takes a document prototype,
      fetches the required parts (getProdParts), checks stock availability and prices for each part,
      and removes items that are not needed or unavailable.
    •	Chain: After processing all parts for a document, its finalize block performs final calculations,
      emits the newly created documents via @Output() onCreateNewDocuments, and optionally triggers the final
      subscription by calling createNewBackDocument$.next() if a return document is needed.

  4.	createNewBackSubscription
    •	Trigger: createNewBackDocument$.next() is called from the finalize block of the createNewManDocumentSubscription.
    •	Purpose: It creates a final document to move the completed products back to a stock from the production site.
      It calculates the cost price of the manufactured items based on the sum of all component movement documents created in the previous steps.
    •	Chain: This is the final step in the automated flow. It pushes the completed "return" document into the newDocuments array.
*/

/*
  Based on the component's design, the subscription flow is intended to be restartable.
  The Subject instances (newDocumentProcess$, a2MoveOptions$, etc.) are long-lived and are not completed during the process, which allows them to be used multiple times.
  To restart the subscription flow, you simply need to set the @Input() command property back to 1.
  How It Works
  1.	Initiation: The entire process begins when ngOnChanges detects that the command input has been set to 1. This calls the processDocItems() method.
  2.	Trigger: processDocItems() calls this.newDocumentProcess$.next(), which pushes a new value into the newDocumentProcess$ subject and starts the chain of subscriptions.
  3.	State Reset: The component is designed to reset its state at the beginning of the flow.
  •	In createA2MoveSubscription, this.newDocuments = []; is called, clearing the results from any previous run.
  •	In createNewDocumentProcessSubscription, this.newDocument.items = []; is called, ensuring the document starts with a clean slate of items for the new run.
  Because the subscriptions are always listening and the internal state is cleared on each run, you can restart the entire sequence by following these steps in the parent component:
  1.	Ensure any data inputs (docItems, newDocument) are updated if necessary.
  2.	Set the command input property to 1.
  This will trigger ngOnChanges and restart the entire document creation process.
*/

type DocItemGroup = { [key: string]: DocItems[] };

@Component({
  selector: 'app-new-document-dlg-process-tran-prod',
  templateUrl: './new-document-dlg-process-tran-prod.component.html',
  styleUrls: ['./new-document-dlg-process-tran-prod.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class NewDocumentDlgProcessTranProdComponent implements OnInit, OnChanges, OnDestroy {

  private gsd: GlobalSettingsData;

  /*
   * Consider that parentDocument may have or may NOT have items property filled with [actual] docItems
   */
  @Input() parentDocument: Document;

  /*
   * Therefore, to keep source object untouched (immutable ????)
   * we will expect alone/independent copy of DocItems as Input parameter
   * 
   */

  @Input() docItems: DocItems[] = null;

  @Input() newDocument: Document = null;

  @Input() newDocuments: Document[] = null;

  public get hasParentDocument(): boolean { return (this.parentDocumentId && true); }

  @Input() command: number = 0;   /* Command to child components what to do
                                   *
                                   * 0 - nothing / prepare
                                   * 1 - go
                                   * 2 - clean
                                   * 3 - reset to initial state
                                   * 
                                   */
  parentDocumentId: number = null;

  childDocuments: Document[] = null;

  sourceDocItems: DocItems[] = null;

  arrayOfParts: DocItemGroup = {};

  blocked: boolean = false;

  stock1: number = null;
  stock2: number = null;
  stock3: number = null;
  stock4: number = null;

  allowImpStock: boolean = true;
  allowAuxStock: boolean = true;
  allowMainStock: boolean = true;

  doNotCheckProduction: boolean = false;    // Do not count previously produced items on main stock
  doNotCheckPrevious: boolean = false;      // Do not count previously moved parts to production site
  moveProductionBack: boolean = true;       // Create document to move completed products back to stock (formerly split across this and allowBackStock)

  productionMode: string = 'm1';

  @Input() selectedActions: any = [1, 3];

  private newDocumentProcess$ = new Subject<number>();
  private createNewDocumentQuery: Subscription = null;

  private a3MoveOptions$ = new Subject<Observable<any>[]>();    // Prototyping only

  private a2MoveOptions$ = new Subject<Observable<{
    o1: boolean, s1: number, o2: boolean, s2: number, o3: boolean, s3: number, s4: number, a1: string, pr: number, op1: boolean, op2: boolean, np: number, op4: boolean
  }>>();
  private _move2ActionSubs: Subscription = null;

  private newDocumentQueue: Document[] = [];

  private createNewManDocument$ = new Subject<{ documents: Document[], product: DocItems }>();
  private _creatNewManDoc: Subscription = null;

  private processManDocuments$ = new Subject<{ documents: Document[], product: DocItems }>();
  private _processManDoc: Subscription = null;

  private createNewBackDocument$ = new Subject<{ document: Document, product: DocItems }>();
  private _creatNewBackDoc: Subscription = null;

  @Output() onCreateNewDocuments = new EventEmitter<Document[]>();

  @Output() onGetTransactionResult = new EventEmitter<any[]>();

  selectedManufacturedItem: number = null;
  numberOfItemsToProduce: number = null;
  maxItemsToProduce: number = null;
  products: { prodid: number; prodname: string; quantity: number; max: number; }[] = [];

  public get thereIsManufacturedItems(): boolean { return (((this.docItems || []).filter(di => (di.PRODKIND == 1))).length > 0); }

  partsCache: Product[] = [];   // Cache to fill in parts data to avoid multiple calls and to correctly count stock for same parts in different target products

  targetCache: Product[] = [];  // Cache to fill in target products data to avoid multiple calls

  // targetPartsCache[prodid]=[{prodid, requiredQty, movedQty}]

  targetPartsCache: {
    prodid: number,
    PRODCODE: string,
    partid: number,
    PARTCODE: string,
    perUnitQty?: number,
    stockQty: number,
    previuosQty: number,
    requiredQty: number,
    movedQty: number
    movedPrice: number;
    movedSum: number;
  }[] = [];

  constructor(
    private restApi: RestApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private cliApi: ClientApiService,
    private prodApi: ProductApiService,
    private messageService: MessageService,
  ) {
    this.createNewBackSubscription();
    this.createNewManDocumentSubscription();
    this.createA2MoveSubscription();
    this.createNewDocumentProcessSubscription();
  }

  /*
   * createNewBackSubscription
   *
   * Return completed products back to stock from production site
   *
   * Here we have prototype of document and we simply need some math
   *
   * Manufacturing is a single atomic operation (agreed with finance): the "return
   * completed product to stock" document is priced ONLY from the move-to-production
   * document(s) created in THIS SAME execution (this.newDocuments) - there is no such
   * thing as an earlier, separate operation whose cost should be folded in here. An
   * earlier version of this code also pulled in any other DOCTYPEID=5 child document of
   * the same parent matching the product code by DOCNOTE2 text, on the theory that a
   * multi-step production (parts short in an earlier pass, finished later) needed its
   * cost blended across runs. That theory doesn't hold: it can just as easily match a
   * document from a completely unrelated prior production of the same product against
   * the same parent, with no way to tell the two apart from DOCNOTE2 text alone -
   * inflating the unit cost with cost that was never part of this operation.
   *
   * 1) Determine price as sum of this execution's own documents only
   * 2) Sanity-check that what was actually moved matches the BOM requirement for what
   *    was produced - see the mismatch check below
   */

  createNewBackSubscription() {
    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewBackSubscription');

    // This procedure called from createNewManDocumentSubscription finalize block
    // everytime we process one product to produce
    // So, if we produce few products - this procedure called few times too
    // But exeqution depends on status of production - complete or partial

    // Another task to process blocked status
    // In general we have release blocked status in finalize block of createNewManDocumentSubscription
    // But after this procedure we have to release it too on the very last call only

    this._creatNewBackDoc =
      this.createNewBackDocument$.pipe(
        map(data => {
          this.blocked = true;
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent createNewBackDocument$ blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));

          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewBackDocument$: G1');
          return data;
        }),

      // May be we need to check if there product intended to produce exists
        filter(data => (data.document != null) && (data.document.items != null) && (data.document.items.length > 0)),

        tap(data => data.document.DOCFLAG = DocumentStatusFlag.EverythingIsOk.value),

        concatMap(data => from(data.document.items).pipe(
          // Iterate through every produced item

          map(di => {

            let u22 = this.newDocuments
              .filter(e => ((data.document.DOCMANDEPID == e.DOCCLIDEPID) && e.DOCNOTE2 && (e.DOCNOTE2.split('"')[1] == di.PRODCODE)))
              .map(e => ({ DOCSUM: e.DOCSUM, DOCRATEVAL: e.DOCRATEVAL }));

            environment.production || console.table(u22);

            let totalCost = u22.reduce((s, e) => s += (e.DOCRATEVAL ? (e.DOCSUM / e.DOCRATEVAL) : 0), 0);

            di.DOCITEMVPRICE = (di.DOCITEMQTY > 0) ? (totalCost / di.DOCITEMQTY) : 0;
            di.DOCITEMPRICE = di.DOCITEMVPRICE * data.document.DOCRATEVAL;
            di.DOCITEMSUM = di.DOCITEMPRICE * di.DOCITEMQTY;

            // BOM sanity check: what was actually moved to the production site for this
            // product in this execution should exactly match the BOM requirement
            // (perUnitQty x produced qty). A mismatch means the cost above is not
            // trustworthy and points at a bug upstream (parts roll-out / stock split),
            // not something to silently accept.
            let mismatches = this.targetPartsCache.filter(tp => (tp.prodid == di.PRODID) && (tp.movedQty != tp.requiredQty));

            if (mismatches.length > 0) {
              environment.production || console.table(mismatches);

              // Money impact per part: totalCost above sums movedQty (not requiredQty) x
              // price for every part, so a part moved short/over by (movedQty - requiredQty)
              // units at the price actually paid for it shifts the backward document's total
              // cost away from the BOM-correct figure by exactly that amount - summed across
              // the mismatched parts, this is the total sum discrepancy for this product.
              let sumDiscrepancyValue = mismatches.reduce((s, m) => s + ((m.movedQty - m.requiredQty) * m.movedPrice), 0);
              let sumDiscrepancyLocal = sumDiscrepancyValue * data.document.DOCRATEVAL;

              this.messageService.add({
                sticky: true,
                severity: 'warn',
                summary: 'Несоответствие BOM ' + di.PRODCODE,
                detail: mismatches.map(m => m.PARTCODE + ': требуется ' + m.requiredQty + ', перемещено ' + m.movedQty
                    + ' (' + ((m.movedQty > m.requiredQty) ? '+' : '') + round2((m.movedQty - m.requiredQty) * m.movedPrice) + ' у.е.)').join('; ')
                  + '. Итого расхождение суммы возврата: ' + ((sumDiscrepancyValue > 0) ? '+' : '') + round2(sumDiscrepancyValue) + ' у.е.'
                  + ' (' + ((sumDiscrepancyLocal > 0) ? '+' : '') + round2(sumDiscrepancyLocal) + ' в валюте документа)',
                life: 180
              });

              // The toast above is easy for a busy manager to dismiss and forget - it leaves
              // no trace once closed. DOCFLAG is the durable signal: set on the document
              // itself (initialized to EverythingIsOk before this loop, escalated - never
              // reset back down - per mismatched item, in case a future change ever puts
              // more than one produced item on the same backward document), it persists to
              // the DB on save and shows up as a red flag wherever documents are listed
              // (documents.component.html's DOCFLAG==0 check is doctype-agnostic),
              // regardless of whether anyone read the toast.
              data.document.DOCFLAG = DocumentStatusFlag.SeriousError.value;
            }
          }),

          finalize(() => {
            environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewBackDocument$ B1: finalize');

            this.newDocuments.push(data.document);
            environment.production || console.table(this.newDocuments);
          })
        )),
    ).subscribe({
      next: next => {
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewBackDocument$ B3: subscribe next: ' + JSON.stringify(next));
      },
      error: err => {
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewBackDocument$ B3: subscribe error: ' + JSON.stringify(err));
        this.messageService.add({ severity: 'error', summary: 'createNewBackDocument$', detail: JSON.stringify(err), life: 30 });
        this.blocked = false;
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
      },
      complete: () => {
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewBackDocument$ B3: subscribe complete: ');
      }
    });
  }

  /*
   * createNewManDocumentSubscription
   *
   * Create document to move item(s) from stocks to production site
   *
   * in final section calls the procedure to move back produced item(s)
   * 
   */
  createNewManDocumentSubscription() {

    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocumentSubscription');

    this._processManDoc = this.processManDocuments$.pipe(

      /*
        * Here we come in with prototype of document with parts (N stock documents X parts docitems in every document))
        * Now we have to check the particular stock where
        * every part exists and what is the price
        * 
        * to avoid multiple calls we will use partsCache[] filled with stock and price for parts
        * 
        * Missing items should be deleted from document content
        * And one more document should be created to return completed products back
        * If corresponding action selected
        * Select it by DOCCLIDEPID
        * TODO: Review below code to more safe and clean style
        * Add 1 seconds to latest document in queue
        * If we have few documents to move
        *
        * THere is one more caveat to correspond particular department and stock in PRODINFO table
        *
        * Here the order of stocks we use to fill PRODINFO table
        * 
        *       this.globalSettinsData.getMainStock(),
        *       this.globalSettinsData.getImpStock(),
        *       this.globalSettinsData.getAuxStock(),
        *
        * So we would need some trick to select correct stock for particular department
        * like if doc.DOCMANDEPID == this.globalSettinsData.auxDepartmentId then use _PRODINFO.stockAUX
        * else if doc.DOCMANDEPID == this.globalSettinsData.impDepartmentId then use _PRODINFO.stockIMP
        * else use _PRODINFO.stockMAIN
        * 
        * TODO: review this code to determine the settings
        */

      tap(docs => {
        if (!environment.production) {
          console.table(this.partsCache);
          console.table(docs.documents);
        }
      }),

      // Iterate through every document prototype (by stock)
      concatMap(docs => from(docs.documents).pipe(
        // DOCCLIDEPID is the configurable assembly/production site (this.stock4), not always
        // gsd.manDepartmentId - the forward move documents built in createA2MoveSubscription use
        // whatever stock4 was set to when this run started.
        filter(doc => (doc.DOCCLIDEPID == this.stock4) && (doc.DOCNOTE2 && doc.DOCNOTE2.includes(docs.product.PRODCODE))),

        tap(doc => {
          environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: process document'
            + ' for PRODCODE ' + docs.product.PRODCODE
            + ': MANDEPID ' + doc.DOCMANDEPID
            + ' => CLIDEPID ' + doc.DOCCLIDEPID
            + ', newDocuments length: ' + this.newDocuments.length
            + ', items length: ' + doc.items.length
          ));

          environment.production || (console.table(doc));
          environment.production || (console.table(doc.items));
        }),

        concatMap(doc => from(doc.items).pipe(

          // Iterate through every part item

          tap(di => environment.production ||
            (console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1 '
              + ' for PRODCODE: ' + docs.product.PRODCODE
              + ' need: ' + di.PRODCODE
              + ' Q: ' + di.DOCITEMQTY
              + ' from stock: ' + doc.DOCMANDEPID))),

          // Check if were previous move for this product to the production site

          concatMap(di => this.prodApi.getProductPartStockBefore(di.PRODID, doc.DOCCLIENTSID, doc.DOCCLIDEPID, doc.DOCDATE).pipe(

            map(stock => {

              let stockbe = (stock as StockResult[])[0]?.stock ?? 0;

              environment.production
                || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1 '
                  + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - On the stock ' + doc.DOCCLIDEPID +' already: ' + stockbe);

              // Entering here we have di.DOCITEMQTY set to required quantity to produce (or really avalable on stock))
              // and stockbe set to quantity possibly moved before to production site
              // Therefore we have to reduce di.DOCITEMQTY by stockbe

              let partRowCache = this.targetPartsCache.find(tp => (tp.prodid == docs.product.PRODID) && (tp.partid == di.PRODID));
              if (partRowCache) {
                partRowCache.previuosQty = stockbe;
              } else {
                this.targetPartsCache.push(
                  {
                    prodid: docs.product.PRODID,
                    PRODCODE: docs.product.PRODCODE,
                    partid: di.PRODID,
                    PARTCODE: di.PRODCODE,
                    previuosQty: stockbe,
                    requiredQty: di.DOCITEMQTY,
                    stockQty: 0,
                    movedQty: 0,
                    movedPrice: 0,
                    movedSum: 0
                  });
              }

              if (!this.doNotCheckPrevious) {
                di.DOCITEMQTY = (di.DOCITEMQTY > stockbe) ? (di.DOCITEMQTY - stockbe) : 0;
              }
              return di;
            }))),

          // CleanUp unneeded items
          // Already moved before or zero quantity to move

          map(di => {

            environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A3 ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY);

            if (di.DOCITEMQTY == 0) {

              let i = doc.items.findIndex(e => e.PRODID == di.PRODID);

              if (i >= 0) {
                doc.items.splice(i, 1);
                environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A3a index:' + i);
                di = null;
              }
            }
            return di;
          }),
          filter(di => di != null),

          // Check is there any source
          //

          // Here whould start the trickier part
          // because we have to check stock on particular source stock
          // and we have overall stock only in partsCache[]
          // so we have to check both - partsCache[] and real stock on source stock

          map(di => {

            // Let's say _PRODUCTINFO.PRODSTOCKM, .PRODSTOCKI, .PRODSTOCKA and .PRODSTOCK contains actual stock on particular stocks
            // Information in _PRODUCTINFO filled by prodApi.getProductInfoById called earlier during parts gathering
            // The order of stocks in PRODINFO is:
            //     this.globalSettinsData.getMainStock(),
            //     this.globalSettinsData.getImpStock(),
            //     this.globalSettinsData.getAuxStock(),

            // therefore we can treat
            // _PRODUCTINFO.PRODSTOCKM as stock for main department
            // _PRODUCTINFO.PRODSTOCKI as stock for import department
            // _PRODUCTINFO.PRODSTOCKA as stock for auxiliary department
            // and _PRODUCTINFO.PRODSTOCK as overall stock (sum of all)

            // We need to check particular stock by DOCMANDEPID

            // Determine available stock on particular source stock
            // then compare with required quantity to move
            // and reduce if needed 

            let availableStock = 0;
            let clearDocItemRow: boolean = true;
            let pcache = this.partsCache.find(p => (p.PRODID == di.PRODID));
            let partRowCache = this.targetPartsCache.find(tp => (tp.prodid == docs.product.PRODID) && (tp.partid == di.PRODID));

            if (partRowCache && ((partRowCache.requiredQty - partRowCache.movedQty) > 0)) {   // Still need some quantity to process

              // Sibling documents for the other stocks were cloned from the same parts array,
              // so di.DOCITEMQTY here is still the FULL per-part requirement, not aware of what
              // an earlier-processed stock (in this concatMap sequence) already moved via
              // partRowCache.movedQty. Clamp to what's actually still outstanding before checking
              // this stock's availability, otherwise a part present on more than one stock gets
              // moved from each of them up to its full original requirement (over-consuming stock
              // and overshooting requiredQty).
              let stillNeeded = partRowCache.requiredQty - partRowCache.movedQty;
              if (di.DOCITEMQTY > stillNeeded) {
                di.DOCITEMQTY = stillNeeded;
              }

              if (pcache && pcache._PRODUCTINFO) {
                environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1k for: ' + docs.product.PRODCODE + ' part: ' + pcache.PRODCODE + ': need on ' + doc.DOCMANDEPID + ' - ' + di.DOCITEMQTY + ', have - '
                  + pcache._PRODUCTINFO.PRODSTOCK + ' (' + pcache._PRODUCTINFO.PRODSTOCKM + ', ' + pcache._PRODUCTINFO.PRODSTOCKI + ', ' + pcache._PRODUCTINFO.PRODSTOCKA + ')');

                partRowCache.stockQty = pcache._PRODUCTINFO.PRODSTOCK;
                if (pcache._PRODUCTINFO.PRODSTOCK > 0) {    // Is there anything on overall stock?

                  // Ok. let's check particular stock by DOCMANDEPID

                  if (doc.DOCMANDEPID == this.gsd.getMainStock()) {
                    availableStock = pcache._PRODUCTINFO.PRODSTOCKM ?? 0;
                    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1k for: ' + docs.product.PRODCODE + ' part: '
                      + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Limited by MAIN stock: ' + availableStock);

                    if (availableStock < di.DOCITEMQTY) {
                      di.DOCITEMQTY = availableStock;
                    }
                    pcache._PRODUCTINFO.PRODSTOCKM = availableStock - di.DOCITEMQTY;
                    clearDocItemRow = false;

                  } else if (doc.DOCMANDEPID == this.gsd.getImpStock()) {
                    availableStock = pcache._PRODUCTINFO.PRODSTOCKI ?? 0;
                    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1k for: ' + docs.product.PRODCODE + ' part: '
                      + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Limited by Imp stock: ' + availableStock);

                    if (availableStock < di.DOCITEMQTY) {
                      di.DOCITEMQTY = availableStock;
                    }
                    pcache._PRODUCTINFO.PRODSTOCKI = availableStock - di.DOCITEMQTY;
                    clearDocItemRow = false;
                  } else if (doc.DOCMANDEPID == this.gsd.getAuxStock()) {
                    availableStock = pcache._PRODUCTINFO.PRODSTOCKA ?? 0;
                    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1k for: ' + docs.product.PRODCODE + ' part: '
                      + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Limited by Aux stock: ' + availableStock);

                    if (availableStock < di.DOCITEMQTY) {
                      di.DOCITEMQTY = availableStock;
                    }
                    pcache._PRODUCTINFO.PRODSTOCKA = availableStock - di.DOCITEMQTY;
                    clearDocItemRow = false;
                  }
                  // Ajust the overall stock too
                  pcache._PRODUCTINFO.PRODSTOCK = pcache._PRODUCTINFO.PRODSTOCK - di.DOCITEMQTY;
                  // Here there is some algorithmic issue - if part exists on multiple stocks, we will reduce overall stock multiple times
                  // as well as move it multiple times
                  // Therefore we have to check actual overall stock from partsCache[] in the end of whole procedure
                  // to determine if production was complete or partial only
                  // Not good
                  // We have to count parts related to particular product and count transfered parts
                  // To determine it transferred enought to produce
                  // Looks like we have to arrange 3rd cache to store info about target products too
                  // something like targetCache[] of Product[]
                  // or targetPartsCache[prodid]=[{prodid, requiredQty, movedQty}]
                  partRowCache.movedQty += di.DOCITEMQTY;

                  environment.production || console.table(this.targetPartsCache);

                  environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1k for: ' + docs.product.PRODCODE + ' part: ' + pcache.PRODCODE + ': di ' + di.DOCITEMQTY);
                }
              }
            } // end of check if still need some quantity to process

            // Above else clauses set di.DOCITEMQTY to 0 if part is absent on source stock or there is no info about part at all

            // CleanUp unneeded items (items not found on particular stock or already moved))

            // Here we know that particular part is absent on source stock
            // Therefore we have to remove it from document
            // In general it is not a problem, because production may be partial
            // and item could be found on another stock
            // But if item is absent on all stocks - production would be incomplete
            // and we have to notify user about this

            // By fact here we have to check if sum of movedQty in targetPartsCache[] is less than requiredQty
            // to notify user about incomplete production

            // So somewhere we have to check if sum of di.DOCITEMQTY in generated documents is less than required to produce
            // partsCache[] should help here - because it contains all parts for all target products
            // therefore we can check it after all documents processed
            //
            // Here we operate with docItems[] - source items to produce
            // But partsCache[] is the type of Product[]
            // Actual stock status is in partsCache[]: partsCache[0]._PRODINFO.PRODSTOCKM, .PRODSTOCKI, .PRODSTOCKA and .PRODSTOCK as sum of all

            if (clearDocItemRow || (di.DOCITEMQTY == 0)) {

              let i = doc.items.findIndex(e => e.PRODID == di.PRODID);

              if (i >= 0) {
                doc.items.splice(i, 1);
                console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A1k part to remove: ' + di.PRODCODE + ', index: ' + i);
                di = null;
              }
            }
            return di;
          }),

          filter(di => di != null),

          // Get price for item

          concatMap(di => this.prodApi.getProdIncomePriceById(di.PRODID, doc.DOCMANCLIID, doc.DOCMANDEPID, doc.DOCCURRID, di.DOCITEMQTY, doc.DOCDATE).pipe(
            map(price => {
              di.DOCITEMVPRICE = (price && (price.length > 0)) ? price[0].PRODINCOMEPRICE : 0;

              di.PRODVAT = (di.PRODVAT ?? this.gsd.vatValue);
              di.DOCITEMPRICE = di.DOCITEMVPRICE * doc.DOCRATEVAL;
              di.DOCITEMPRICEWOVAT = round2(di.DOCITEMPRICE / ((100.0 + di.PRODVAT) / 100.0));
              di.DOCITEMSUM = di.DOCITEMPRICE * di.DOCITEMQTY;

              environment.production
                || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A4 '
                  + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Check source price: ' + price[0].PRODINCOMEPRICE);

              let partRowCache = this.targetPartsCache.find(tp => (tp.prodid == docs.product.PRODID) && (tp.partid == di.PRODID));
              if (partRowCache) {
                partRowCache.movedPrice = di.DOCITEMVPRICE;
              } 
            })
          )),
        )),

        finalize(() => {

          // Finalize iteration through documents for stock
          // Remove inactive documents
          // Update active documents sum before pushing
          // If selected - create document to move completed products back

          // Somewhere here we have to check if something is left to move
          // This would mean that items not enough to manufacture anything
          // and therefore we should not finish process and we need to add option - partial manufacture
          // TODO: add this option

          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: finalize and process documents fill in');
          environment.production || console.table(this.newDocuments);
          environment.production || console.table(this.partsCache);

          /*
            * Here we have all the documents' prototype with parts
            * Now we have to check where every part exists and what is the price
            * Missing items should be deleted from document content
            *
            * And one more document should be created to return completed products back
            * If corresponding action selected
            *
            * Select it by DOCCLIDEPID
            */

          environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A4 - documents in queue: ' + this.newDocuments.length));

          // Remove inactive documents
          this.newDocuments = [...this.newDocuments.filter(e => e.items.length > 0)];

          // Update active documents sum before pushing

          this.newDocuments.forEach(d => (d.DOCSUM = d.items.reduce((s, e) => s += e.DOCITEMSUM, 0)));

          // TODO: Here we have to check the targetPartsCache[] to determine overall production status
          // and notify user if something could not be produced due to lack of parts on source stocks
          // And after that we have to issue the this.createNewBackDocument$

          // There is an issue here - we don't count items moved before to production site

          let incompleteProduction: boolean = false;

          this.targetPartsCache.filter(tp => (tp.prodid == docs.product.PRODID)).forEach(tp => {
            tp.movedSum = tp.movedPrice * tp.movedQty;

            if ((tp.movedQty + tp.previuosQty) < tp.requiredQty) {
              incompleteProduction = true;
              environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A4 - incomplete production for product '
                + tp.PRODCODE + ' part ' + tp.PARTCODE
                + ': required ' + tp.requiredQty + ', moved ' + tp.movedQty);
            }
          });

          if (incompleteProduction) {
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: 'Неполное производство ' + docs.product.PRODCODE,
              detail: 'Некоторые изделия не могут быть произведены в полном объеме из-за отсутствия комплектующих на складах источниках.',
              life: 180
            });
          }

          //  Add 1 seconds to latest document in queue
          //
          if (!incompleteProduction && this.moveProductionBack) {

            environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$: Finalize A4 - create moveBack document');

            // check the tail of newDocuments array to find lates document
            // Add one second to create new document after all
            let bd = new Date();

            let dt = this.newDocuments.slice(-1);

            environment.production || console.table(dt);

            if (dt[0]) {
              bd = new Date(dt[0].DOCDATE);
              bd = new Date(bd.getFullYear(), bd.getMonth(), bd.getDate(), bd.getHours(), bd.getMinutes(), bd.getSeconds() + 1);
            }

            // Looks like here is the root of problem with items
            // because we use parentDocument to get items
            // but parentDocument may not have items at all (why ???))
            // therefore we have to use this.docItems instead
            // this.newDocument.items = [...((this.docItems || []).filter(di => (di.PRODKIND == 1)))];
            // TODO: review this code to determine the settings

            // Here we have to limit the docItems by single manufactured item (the one we process now)
            // because newDocument is template for all manufactured items

            //let dis = this.newDocument.items.find(di => (di.PRODKIND == 1));
            //let dp = arguments.callee.caller.arguments[0].filter(di => (di.PRODKIND == 1) && (di.PRODCODE == dis.PRODCODE));

            this.createNewBackDocument$.next({document: {

              ... this.newDocument,

              ...{
                DOCDATE: bd,
                DOCEXPDATE: bd,
                DOCVALDATE: bd,

                // DOCCLIDEPID (destination) always returns to the predefined main stock.
                // DOCMANDEPID (source) is the configurable assembly/production site
                // (this.stock4), matching the same site the forward move documents above
                // delivered parts to.
                DOCCLIDEPID: this.globalsettingsservice.getGlobalSettings().mainStockId,
                DOCMANDEPID: this.stock4,
                DOCNOTE2: 'Возврат из производства ' + docs.product.DOCITEMQTY + ' шт \"' + docs.product.PRODCODE + '\"',

                items: [docs.product]
              }
            }, product: docs.product});
          }
          this.onCreateNewDocuments.emit(this.newDocuments);
          this.onGetTransactionResult.emit(this.targetPartsCache);

          environment.production || console.table(this.newDocuments);

          this.blocked = false;
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent processManDocuments$ blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
        })
      )),
  ).subscribe({
    next: next => {
      environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$ E3: subscribe next: ' + JSON.stringify(next));
    },
    error: err => {
      environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$ E3: subscribe error: ' + JSON.stringify(err));
      this.messageService.add({ severity: 'error', summary: 'processManDocuments$', detail: JSON.stringify(err), life: 30 });
      this.blocked = false;
      environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
    },
    complete: () => {
      environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in processManDocuments$ E3: subscribe complete: ');
    }
  });

    // here we process every document prototype made in a2MoveOptions$
    // to fill in by parts and check stock / price
    // We came here with items to produce and decompose them to parts
    // Parts with zero quantity on the particular stock will be removed
    // After this procedure has been done we would have newDocuments[] filled in by actual documents
    // to move items to production site

    // Latest update from 27-10-2025
    // Here we come already with documents contain decomposed parts
    // Therefore we need only to check stock and price for every part item
    // Remove items with zero stock on particular source stock
    // By fact we already have all stock information im partsCache[] and in docitems._PRODINFO

  this._creatNewManDoc =
    this.createNewManDocument$.pipe(

      tap(() => {
        this.blocked = true;
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent createNewManDocument$ blocked debug BLOCKED: ' + (((this.blocked) ? '** ON ***' : '** OFF **')));
      }),

    // Here we simply iterate through documents in queue
    // and fill in missing fields
    // Than push document prototype to newDocuments[]
    //
    // Decompose of manufactured item to parts already done in a2MoveOptions$
      concatMap(dq => from(dq.documents).pipe(
        map(doc => {
          environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$: G1 process document'
            + ' for PRODCODE ' + dq.product.PRODCODE
            + ': MANDEPID ' + doc.DOCMANDEPID
            + ' => CLIDEPID ' + doc.DOCCLIDEPID
            + ', newDocuments length: ' + this.newDocuments.length
            + ', items length: ' + doc.items.length
            + ', DOCNOTE: ' + doc.DOCNOTE2
          ));

          environment.production || (console.table(doc));
          environment.production || (console.table(doc.items));

          // Document's array could have 1 to 3 documents to move items from different stocks
          // We unable to know how many documents will be created

          // Single point to isolate document prototype

          let newNdc: Document = doc;

          this.newDocuments.push(newNdc);  // Push document prototype to output array to keep it in memory

          return newNdc;
        }),

        /*
          * Fill in missing fields in document prototype
          */
        concatMap(ndc => this.cliApi.getChildDepartmentsById(ndc.DOCCLIDEPID, ndc.DOCPCOCLIID).pipe(
          tap(d => environment.production
            || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$: getChildDepartmentsById: check DOCPCOCLIID: '
              + ndc.DOCPCOCLIID + ', ndc.DOCCLIDEPID: ' + ndc.DOCCLIDEPID + ', dep: ' + JSON.stringify(d))),

          map(d => {

            // Fill in PCODEPID (on CLIDEPID)

            if (d && d.length > 0) {
              ndc.DOCPCODEPID = d[0].CLIID;
            }
            return ndc;
          }))),

        concatMap(ndc => this.cliApi.getChildDepartmentsById(ndc.DOCMANDEPID, ndc.DOCLCOCLIID).pipe(
          tap(d => environment.production
            || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$: getChildDepartmentsById: check DOCLCOCLIID: '
              + ndc.DOCLCOCLIID + ', ndc.DOCMANDEPID: ' + ndc.DOCMANDEPID + ', dep: ' + JSON.stringify(d))),

          map(d => {

            // Fill in LCODEPID (on MANDEPID)

            if ((d && (d.length > 0))) {
              ndc.DOCLCODEPID = d[0].CLIID;
            }
            return ndc;
          }))),

        // Just to check document prototype

        finalize(() => {
          environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$: G6: finalize '));
          environment.production || (console.table(this.newDocuments));

          this.processManDocuments$.next({ documents: this.newDocuments, product: dq.product });

          this.onGetTransactionResult.emit(this.targetPartsCache);

        })
      )), // End of from(dq
    )
    .subscribe({
      next: next => {
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$ B3: subscribe next: ' + JSON.stringify(next));
      },
      error: err => {
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$ B3: subscribe error: ' + JSON.stringify(err));
        this.messageService.add({ severity: 'error', summary: 'createNewManDocument$', detail: JSON.stringify(err) });
        this.blocked = false;
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
      },
      complete: () => {
        environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$ B3: subscribe complete: ');
      }
    }); // this.createNewManDocument$
  }

  /*
   * createA2MoveSubscription
   * 
   * This is action to move items to manufacturing "site" from main, import and auxilliary stocks
   * As well as return it back after production
   *
   * Action generates few new documents in queue to fill in by corresponding items
   * 
   * Here we will create NxM documents,
   * where
   * N - 1,2 or 3 stocks to search components,
   * and
   * M - number of items to produce
   * 
   * Least procedure will save only non-empty documents (document.items.count > 0)
   *
   *
   * It is not clear why we need complete() here
   * 
   * calls createNewManDocument$.next() few times and createNewManDocument$.complete()
   * to initiate final procedures
   * 
   */
  createA2MoveSubscription() {
    /*
     *
     */
    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createA2MoveSubscription');
    let bd: Date;
    let timeGap: number = 1;   // Initial time shift in seconds from selected date;

    this._move2ActionSubs =
      this.a2MoveOptions$
        .pipe(
          debounceTime(100),

          switchMap(o => {
            environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: G0'));

            bd = new Date(this.newDocument.DOCDATE);
            timeGap = 1;

            // Clear output array only once per procedure
            // Because this.newDocuments contains all document prototypes to create
            // in the end we push it to // here we need to think how to manage multiple calls

            this.newDocuments = [];

            // this.newDocumentProcess$.next() called only once per user action. Calling the this.a2MoveOptions$.next()
            // this.a2MoveOptions$.next() called only once per user action. CAlling the this.createNewManDocument$.next()
            // this.createNewManDocument$.next() called multiple times - once per product to manufacture - very short procedure to align the document prototype
            // this.processManDocuments$.next() called multiple times - once per product to manufacture and bear all the stock operations and cleanup

            this.onCreateNewDocuments.emit(this.newDocuments);
            this.arrayOfParts = {};

            environment.production || console.table(this.newDocument.items);

            return o;
          }),

          /* Here we have newDocument.items filled with items to produce
           * Now we have to check where every item exists and what is the price
           * Missing items should be deleted from document content
           * And one more document should be created to return products back after production is done
           */
          concatMap(o => from(this.newDocument.items).pipe(
            filter(di => (di && (di.PRODKIND == 1) && (di.DOCITEMQTY > 0))),    // Honestly it is double check, but who knows ...

            filter(di => ((o.a1 == 'm1') || ((o.a1 == 'm2') && (o.pr == di.PRODID)))), // Move on with single product or all products

            concatMap(di => this.prodApi.getProductStockBefore(di.PRODID, this.parentDocument.DOCMANCLIID, this.parentDocument.DOCMANDEPID, new Date()).pipe(
              map(result => {
                if (o.a1 == 'm2') {
                  di.DOCITEMQTY = o.np;
                }

                // Only needed if result itself could be a non-array truthy value
                // (e.g. an object instead of an array) — unlikely given StockResult[] typing
                // (stock && stock.length > 0 && stock[0].stock != null) ? stock[0].stock : 0;

                di.DOCITEMGLOBALSTOCK = (result as StockResult[])[0]?.stock ?? 0;
                environment.production
                  || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: process '
                    + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Check target stock: ' + di.DOCITEMGLOBALSTOCK);

                // Look for only shortage quantity
                // But we would need also count quantities required by another users (another documents))
                // Something like count di.SOLD += all documents with this item for selected date

                di.DOCITEMQTY = (di.DOCITEMQTY > di.DOCITEMGLOBALSTOCK) ? (di.DOCITEMQTY - di.DOCITEMGLOBALSTOCK) : 0;

                return di;
              }))),

            tap(di => environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: G1: ' + di.PRODCODE))),

            filter(di => (di.DOCITEMQTY > 0)),  // Continue only if we really need this product on main stock

            // Create document prototype(s) for every item to produce
            // Here we create 1 to 3 documents to move item from different stocks
            // We operate with source product


            // Here should be main game changer - roll out product components
            // and prepare single copy of DocItems array to insert into every document prototype
            // This allow to check the stock only once per part to avoid multiple calls
            //
            // Hovever this would require some more complicated logic to correctly count stock if same part used in different manufactured items
            // and to correctly reflect this in transactionResult[]
            //
            // Also we would need check the stock for every part on particular stock
            // or use very sofisticated partsCache to correctly count stock for every part

            map(di => {
              this.arrayOfParts[di.PRODCODE] = [];

              // We clear document queue here because we going to create new set of documents for every manufactured item
              this.newDocumentQueue = []; 

              return di;
            }),

            concatMap(di => this.prodApi.getProdParts(di.PRODID).pipe(
              tap(parts => environment.production
                || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: roll out ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Parts: ' + parts.length)),
              concatMap(parts => from(parts).pipe(
                map(part => {
                  environment.production
                    || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: roll out parts '
                      + di.PRODCODE + ' Q: ' + di.DOCITEMQTY + ' - Part: ' + part.PARTCODE + ', PQ: ' + part.QUANTITY);

                  this.arrayOfParts[di.PRODCODE].push({
                    DOCITEMID: -2,
                    cloneIndex: 0,
                    DOCID: -2,
                    U4ETKA: null,
                    PRODID: part.PARTID,
                    PRODCODE: part.PARTCODE,
                    DOCITEMPRODNAME: part.PARTNAME,
                    DOCITEMCURRID: this.newDocument.DOCCURRID,
                    DOCITEMCRATE: this.newDocument.DOCRATEVAL,
                    PAYITEMCLIID: 0,
                    DOCITEMPRICE: 0,
                    DOCITEMVPRICE: 0,
                    DOCITEMQTY: part.QUANTITY * di.DOCITEMQTY,
                    DOCITEMSUM: 0,
                    DOCITEMTEXT1: null,
                    DOCITEMTEXT2: null,
                    PAYITEMDEPID: 0,
                    CURCODE: ''
                  });
                  return part;
                }),

                concatMap(part => this.prodApi.getProductById(part.PARTID, this.newDocument.DOCDATE).pipe(
                  // Build parts cache to avoid multiple calls
                  // Antoher role to correctly count stock if the same part used in different manufactured items
                  // And filter doesn't help there

                  filter(pr => (pr != null) && (pr.length > 0)),
                  map(pr => pr[0]),

                  tap(pr => {

                    let prodIndex = this.partsCache.findIndex(p => p.PRODID == pr.PRODID)
                    if (prodIndex >= 0) {
                      // This mean part already in cache - we need only to update stock
                      environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in createNewManDocument$: G4: part cache update ' + pr.PRODCODE));

                      this.partsCache[prodIndex].PRODSOLD += part.QUANTITY * di.DOCITEMQTY;
                    }

                    let prodRow = this.targetPartsCache.find(tpr => ((tpr.prodid == di.PRODID) && (tpr.partid == pr.PRODID)));

                    if (!prodRow) {
                      this.targetPartsCache.push({
                        prodid: di.PRODID,
                        PRODCODE: di.PRODCODE,
                        partid: pr.PRODID,
                        PARTCODE: pr.PRODCODE,
                        perUnitQty: part.QUANTITY,
                        requiredQty: part.QUANTITY * di.DOCITEMQTY,
                        stockQty: 0,
                        previuosQty: 0,
                        movedQty: 0,
                        movedPrice: 0,
                        movedSum: 0
                      });
                    } else {
                      prodRow.requiredQty += part.QUANTITY * di.DOCITEMQTY;
                    }
                    environment.production || (console.table(this.targetPartsCache));
                  }),

                  filter(pr => (this.partsCache.findIndex(p => p.PRODID == pr.PRODID) < 0)),  // Not in cache, let's add

                  concatMap(pr => this.prodApi.getProdInfoById(pr.PRODID,
                    this.newDocument.DOCMANCLIID,
                    this.newDocument.DOCCURRID,
                    this.gsd.getMainStock(),
                    this.gsd.getImpStock(),
                    this.gsd.getAuxStock(),
                    this.newDocument.DOCDATE
                  ).pipe(

                   map(pi => {
                    if ((pi != null) && (pi.length > 0)) {
                      pr._PRODUCTINFO = pi[0];
                      pr.PRODSOLD = part.QUANTITY * di.DOCITEMQTY;

                      this.partsCache.push(pr);
                    }
                   }))))),
              )),

              // Very vital to have this here to finalize parts roll out before creating documents
              // It works like finalize but allows to return data on the next step

              last(),

              map(() => {
                environment.production || (console.table(this.arrayOfParts));

                return {
                  toDo: di, parts: this.arrayOfParts[di.PRODCODE]
                };
              })
            )),

            tap(serv => environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: G2: ' + JSON.stringify(serv)))),

            //In your code, you have an array of objects called parts.
            //  •	Original problem([...parts]): This created a new array but filled it with references to the exact same item objects
            // from the original parts array.When your code later modified an item(e.g., doc.items.splice(...)),
            // it was removing it from an array that was shared by multiple document prototypes, causing unexpected behavior.
            //  •	The fix(parts.map(p => ({ ...p }))): This is a clever and efficient solution for your use case.
            //    1.	parts.map(...) creates a brand new array.
            //    2.	For each part p in the original array, ({ ...p }) creates a brand new object and copies the properties from p.
            //  Since your DocItems objects contain primitive values(numbers, strings), this effectively creates a deep enough copy
            // for your needs, ensuring each new document gets its own independent array of item objects.
            //
            // npm install lodash npm install @types/lodash --save-dev
            //
            // import { cloneDeep } from 'lodash';

            //  const original = {
            //    id: 1,
            //    items: [{ name: 'Item 1' }],
            //    onSave: () => console.log('Saved!')
            //  };

            //  const deepCopy = cloneDeep(original);

            //  // The function is preserved!
            //  deepCopy.onSave(); // "Saved!"

            // cloneDeep(document) moved to next step to cover whole document object just before the pushing to queue

            map(serv => {

              let di = serv.toDo;
              let parts = serv.parts;

              this.newDocumentQueue = [];

              if (o.o1) {

                environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: process O1 ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY);

                bd = new Date(bd.getFullYear(), bd.getMonth(), bd.getDate(), bd.getHours(), bd.getMinutes(), bd.getSeconds() + timeGap++);

                this.newDocumentQueue.push(cloneDeep({

                  ... this.newDocument,

                  DOCDATE: bd,
                  DOCEXPDATE: bd,
                  DOCVALDATE: bd,

                  DOCCLIDEPID: o.s4,
                  DOCMANDEPID: o.s1,
                  DOCNOTE2: 'Передача для производства ' + di.DOCITEMQTY + ' шт \"' + di.PRODCODE + '\" (' + o.s1 + ')',
                  items: parts
                }));
              }

              if (o.o2) {

                environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: process O2 ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY);

                bd = new Date(bd.getFullYear(), bd.getMonth(), bd.getDate(), bd.getHours(), bd.getMinutes(), bd.getSeconds() + timeGap++);

                this.newDocumentQueue.push(cloneDeep({

                  ... this.newDocument,

                  DOCDATE: bd,
                  DOCEXPDATE: bd,
                  DOCVALDATE: bd,

                  DOCCLIDEPID: o.s4,
                  DOCMANDEPID: o.s2,
                  DOCNOTE2: 'Передача для производства ' + di.DOCITEMQTY + ' шт \"' + di.PRODCODE + '\" (' + o.s2 + ')',

                  items: parts
                }));
              }

              if (o.o3) {

                environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: process O3 ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY);

                bd = new Date(bd.getFullYear(), bd.getMonth(), bd.getDate(), bd.getHours(), bd.getMinutes(), bd.getSeconds() + timeGap++);

                this.newDocumentQueue.push(cloneDeep({

                  ... this.newDocument,

                  DOCDATE: bd,
                  DOCEXPDATE: bd,
                  DOCVALDATE: bd,

                  DOCCLIDEPID: o.s4,
                  DOCMANDEPID: o.s3,
                  DOCNOTE2: 'Передача для производства ' + di.DOCITEMQTY + ' шт \"' + di.PRODCODE + '\" (' + o.s3 + ')',

                  items: parts
                }));
              }

              // o.s4 (stock4) is the assembly/production site itself, not a 4th independent
              // source stock - it's used above as DOCCLIDEPID for every forward move document,
              // and as DOCMANDEPID for the backward "return to stock" document built in
              // processManDocuments$'s finalize (gated on moveProductionBack alone). There is
              // nothing further to do with it here.

              // Call this as many time as we have items to produce
              environment.production || console.table(this.newDocumentQueue);

              // We are leaving here with array of documents to process further
              // Documents contain the parts to move from different stocks

              // In the next procedure we have to check every part item on stock
              // as well as count moved quantity in transactionResult[]
              // and finally decide einther manufacturing finished successfully or not

              // Somewhere we have to report the status of manufacturing
              // Keeping in mind we would have multiple documents to manufacture single item

              // We are calling createNewManDocument$ to process every item to produce separately
              // As many times as we have different products to manufacture in source document

              // this.newDocumentQueue contain 1 to 3 document prototypes to move parts from different stocks
              // for particular manufactured item

              this.createNewManDocument$.next({ documents: this.newDocumentQueue, product: di });
            }),

            finalize(() => {
              environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$: G2: finalize iteration '));
            })

          ))
        )
      .subscribe({
        next: next => {
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$ B3: subscribe next: ' + JSON.stringify(next));
        },
        error: err => {
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$ B3: subscribe error: ' + JSON.stringify(err));
          this.messageService.add({ severity: 'error', summary: 'a2MoveOptions$', detail: JSON.stringify(err) });
          this.blocked = false;
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
        },
        complete: () => {
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in a2MoveOptions$ B3: subscribe complete: ');
        }
      });
  }

  /*
   * createNewDocumentProcessSubscription
   *
   * Prepare newDocument content as template for further procedures
   * Check target stock to avoid excess movements
   *
   * Finally calls a2MoveOptions$.next() with all user settings
   * 
   */
  createNewDocumentProcessSubscription() {

    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in createNewDocumentProcessSubscription');

    let rowSortIndex: number;

    this.createNewDocumentQuery =
      this.newDocumentProcess$
        .pipe(
          debounceTime(100),
          map(currencyId => {
            environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$: G0'));
            this.blocked = true;
            environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent newDocumentProcess$ blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
            this.partsCache = [];

            return currencyId;
          }),

          concatMap(currencyId => this.restApi.getCurrencyInfoByID(currencyId).pipe(

            concatMap(rate => {
              this.newDocument.DOCRATEVAL = rate[0].CURRATE;
              this.newDocument.items = [];
              rowSortIndex = 10;

              // Here we are processing the list of items to produce
              // Check either item is manufactured product and quantity to produce is greater than zero
              // Process "emty items option" included
              //
              return from((this.sourceDocItems || []).filter(di => ((di.PRODKIND == 1) && (this.selectedActions.includes(10) || (di.DOCITEMQTY > 0)))));
            }),

            /*
             * Prepare new DocItems based on source items
             * with actual currency rate and prices
             */
            map(di => {
              environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$: G1: ndi ' + di.PRODCODE + ' Q: ' + di.DOCITEMQTY);

              let ndi: DocItems = {
                ...di,
                ... {
                  DOCITEMID: -2,
                  cloneIndex: 0,
                  U4ETKA: null,
                  DOCITEMCURRID: this.newDocument.DOCCURRID,
                  DOCITEMCRATE: this.newDocument.DOCRATEVAL,
                  PAYITEMCLIID: rowSortIndex,
                  PRODVAT: di.PRODVAT ?? this.gsd.vatValue,
                }
              };

              rowSortIndex += 10;

              let kVat = ((100.0 + ndi.PRODVAT) / 100.0); // <= 1,2 when VAT is 20%

              ndi.DOCITEMPRICEWOVAT = round1((di.DOCITEMVPRICE * this.newDocument.DOCRATEVAL) / kVat);
              ndi.DOCITEMPRICE = round2(ndi.DOCITEMPRICEWOVAT * kVat);
              ndi.DOCITEMVPRICE = di.DOCITEMPRICE / this.newDocument.DOCRATEVAL;

              if (this.selectedActions.includes(1)) {

                ndi.DOCITEMVPRICE = di.DOCITEMVPRICE;

                ndi.DOCITEMPRICEWOVAT = round1((ndi.DOCITEMVPRICE * this.newDocument.DOCRATEVAL) / kVat);
                ndi.DOCITEMPRICE = round2(ndi.DOCITEMPRICEWOVAT * kVat);
              }

              ndi.DOCITEMSUM = ndi.DOCITEMPRICE * ndi.DOCITEMQTY;

              if (this.selectedActions.includes(2)) {
                /*
                 * TODO: Consider whether we have to recalculate the U4ETKA value with actual currency rate
                 */
                ndi.U4ETKA = di.U4ETKA;
              }
              return ndi;
            }),

            tap(ndi => environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$: G2: ' + ndi.PRODCODE)),

            /*
             * Make some pre-flight check to avoid unneccesary further processes
             *
             * 1) Check is there already items to be produced are on the stock (NOTE: this check moves to a2MoveOptions$ sequence))
             * 2) Check is there item already shipped
             * 3) Something else
             */
            concatMap(ndi => this.prodApi.getProductShippedByDoc(ndi.PRODID, this.parentDocument.DOCID, new Date()).pipe(

              map(shipped => {

                ndi.DOCITEMSHIPPED = (shipped && (shipped.length > 0)) ? shipped[0].DOCSHIPPEDITEMS : 0;

                environment.production
                  || console.log('*** NewDocumentDlgProcessTranProdComponent in _preFlightCheck: process '
                    + ndi.PRODCODE + ' Q: ' + ndi.DOCITEMQTY + ' - Shipped Items: ' + ndi.DOCITEMSHIPPED);

                // Look for only shortage quantity
                if (!this.doNotCheckProduction) {
                  ndi.DOCITEMQTY = (ndi.DOCITEMQTY > ndi.DOCITEMSHIPPED) ? (ndi.DOCITEMQTY - ndi.DOCITEMSHIPPED) : 0;
                }

                if (ndi.DOCITEMQTY == 0) {
                  this.messageService.add({
                    sticky: true,
                    severity: 'info',
                    summary: 'Внимание',
                    detail: 'По изделию ' + ndi.PRODCODE + ' уже отгружено ' + ndi.DOCITEMSHIPPED + ' шт. Нечего производить.',
                    life: 120
                  });
                }

                return ndi;
              })
            )),

            filter(ndi => (ndi.DOCITEMQTY > 0)),  // Continue only if we really need for THIS product for THIS document on main stock

            // tap(ndi => environment.production || (console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$: G3: ' + ndi.PRODCODE))),

            concatMap(ndi => this.prodApi.getProdInfoById(
              ndi.PRODID, this.newDocument.DOCMANCLIID, this.newDocument.DOCCURRID,
              //
              // It depends on document type ????
              //
              this.gsd.getMainStock(), this.gsd.getImpStock(), this.gsd.getAuxStock(),
              //
              // Is it on actual date or document date
              // Could depends on....
              //
              new Date()
            )
              .pipe(
                map(data => {
                  if (data && data.length > 0) {
                    ndi.prodInfo = data[0];
                  }

                  environment.production
                    || console.log('*** NewDocumentDlgProcessTranProdComponent in getProdInfoById: process '
                      + ndi.PRODCODE + ' Q: ' + ndi.DOCITEMQTY + ' - ProdInfo: ' + JSON.stringify(data));
                  //
                  //  Finally, after all succesfull checking we could push item to newDocument Items array,
                  //

                  this.newDocument.items.push(ndi);

                  environment.production || console.table(this.newDocument.items);
                }),

                /*
                 * Here virtually is the starting point the main procedure to move items to production site
                 *
                 * All preflight checks are done and newDocument is prepared as template
                 */

                finalize(() => {
                  environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$ - Finalize');

                })
              )),

            finalize(() => {
              environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$ outer loop - Finalize');

              // Here there is vital improvement (i think so ...)
              // a2MoveOptions$ parameter changed to Observable<Options>
              // This would allow avoid the calling Subject.complete() method

              this.a2MoveOptions$.next(of({
                o1: this.allowImpStock,
                o2: this.allowAuxStock,
                o3: this.allowMainStock,
                s1: this.stock1,
                s2: this.stock2,
                s3: this.stock3,
                s4: this.stock4,
                op1: this.doNotCheckPrevious,
                op2: this.moveProductionBack,
                a1: this.productionMode,
                pr: this.selectedManufacturedItem,
                np: this.numberOfItemsToProduce,
                op4: this.doNotCheckProduction
              }));
            })
          )),
        )
      .subscribe({
        next: next => {
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$ B3: subscribe next: ' + JSON.stringify(next));
        },
        error: err => {
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$ B3: subscribe error: ' + JSON.stringify(err));
          this.messageService.add({ severity: 'error', summary: 'newDocumentProcess$', detail: JSON.stringify(err) });
          this.blocked = false;
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent blocked debug BLOCKED: ' + ((this.blocked) ? '** ON ***' : '** OFF **'));
        },
        complete: () => {
          environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent in newDocumentProcess$ B3: subscribe complete: ');
        }
      });
  }

  setInitialValues() {
    console.log('*** NewDocumentDlgProcessTranProdComponent setInitialValues');

    this.stock1 = this.gsd.impStockId;
    this.stock2 = this.gsd.auxStockId;
    this.stock3 = this.gsd.getMainStock();
    this.stock4 = this.gsd.manDepartmentId;

    this.allowImpStock = this.gsd.impCountFlag ? true : false;
    this.allowAuxStock = this.gsd.auxCountFlag ? true : false;
    this.allowMainStock = true;
    this.doNotCheckPrevious = false;
    this.moveProductionBack = true;
    this.productionMode = 'm1';

    this.arrayOfParts = {};
    this.partsCache = [];
    this.targetPartsCache = [];

    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent setInitialValues ' + this.allowImpStock);
    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent setInitialValues ' + this.allowAuxStock);
    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent setInitialValues ' + this.allowMainStock);
    environment.production || console.log('*** NewDocumentDlgProcessTranProdComponent setInitialValues ' + this.moveProductionBack);
  }

processDocItems() {
    console.log('*** NewDocumentDlgProcessTranProdComponent in processDocItems');

    /*
     * Here we come with newDocument prepared in createNewDocument initial procedure
     *
     * Furthe we have to process document content
     * to prepare the inital template for further processing
     *
     * Target stock already set
     * 
     * Get Actual currency rate for newDocument
     * And process all items when Currency Rate become available
     * 
     * Those one in newDocument.DOCRATEVAL from createNewDocument Dialog would arrive later
     */
    this.newDocumentProcess$.next(this.newDocument.DOCCURRID);
  }

  ngOnChanges(changes: SimpleChanges) {
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** NewDocumentDlgProcessTranProdComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));

      switch (propName) {

        case 'command':
          switch (change.currentValue) {

            case 0:
              break;

            case 1:
              this.processDocItems();
              break;

            case 2:
              this.newDocuments = [];
              this.onCreateNewDocuments.emit(this.newDocuments);
              break;

            case 3:
              this.setInitialValues();
              break;

          }
          break;

        case 'newDocuments':
          break;

        case 'newDocument':
          if (this.newDocument) {
          }
          break;

        case 'docItems':
          if (this.docItems) {
            this.sourceDocItems = [... this.docItems];

            this.products =
              Array.from(
                new Set(
                  (this.sourceDocItems || [])
                    .filter(di => (di.PRODKIND == 1))
                    .map(v => v.PRODID)
                )
              )
                .map(id => (
                  {
                    prodid: id,
                    prodname: this.sourceDocItems.find(v => id == v.PRODID).PRODCODE,
                    quantity: this.sourceDocItems.find(v => id == v.PRODID).DOCITEMQTY,
                    max: this.sourceDocItems.find(v => id == v.PRODID).DOCITEMQTY,
                  }));
          }
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** NewDocumentDlgProcessTranProdComponent in ngOnDestroy')

    if (this.createNewDocumentQuery) { this.createNewDocumentQuery.unsubscribe(); }
    if (this._move2ActionSubs) { this._move2ActionSubs.unsubscribe(); }
    if (this._creatNewManDoc) { this._creatNewManDoc.unsubscribe(); }
    if (this._processManDoc) { this._processManDoc.unsubscribe(); }
    if (this._creatNewBackDoc) { this._creatNewBackDoc.unsubscribe(); }
  }

  ngOnInit(): void {
    console.log('*** NewDocumentDlgProcessTranProdComponent in ngOnInit');

    /*
     * TODO: Make subscription to newDocument currency rate, because it is quite complex task to wait the set the rate in the main thread
     * Potentially problematic due to different sources ??????
     */

    this.gsd = this.globalsettingsservice.getGlobalSettings();
    console.log('*** NewDocumentDlgProcessTranProdComponent in ngOnInit: getGlobalSettings ' + JSON.stringify(this.gsd));

    this.setInitialValues();
  }

  onDocTypeChanges(event) {
    console.log('*** NewDocumentDlgProcessTranProdComponent onDocTypeChanges ' + JSON.stringify(event));

    if (event.value.includes(1)) {
      console.log('*** NewDocumentDlgProcessTranProdComponent doCurrencyBased document ' + JSON.stringify(event.value));
      /*
       * TODO: Check whether currency of newDocument equal to those in parentDocument
       *       May be we need more sofisticated parameters checking
       */
    }
    if (event.value.includes(2)) {
      console.log('*** NewDocumentDlgProcessTranProdComponent copy U4etka info' + JSON.stringify(event.value));
    }
  }

  stock1Selected(event) {
    console.log('*** NewDocumentDlgProcessTranProdComponent stock1Selected ' + JSON.stringify(event));

    this.stock1 = event;
  }

  stock2Selected(event) {
    console.log('*** NewDocumentDlgProcessTranProdComponent stock2Selected ' + JSON.stringify(event));

    this.stock2 = event;
  }

  stock3Selected(event) {
    console.log('*** NewDocumentDlgProcessTranProdComponent stock3Selected ' + JSON.stringify(event));

    this.stock3 = event;
  }

  stock4Selected(event) {
    console.log('*** NewDocumentDlgProcessTranProdComponent stock4Selected ' + JSON.stringify(event));

    this.stock4 = event;
  }

  // Handlers for production item selection and number of items to produce

  onSelectedItemChange(event) {
    console.log('*** NewDocumentDlgProcessSpecComponent onSelectedItemChange ' + JSON.stringify(event));

    environment.production || console.table(this.products);
    environment.production || console.table(this.newDocument.items);

    let si = this.products.find(p => p.prodid == event.value);
    this.numberOfItemsToProduce = (si) ? si.quantity : 1;
    this.maxItemsToProduce = (si) ? si.max : 1;
  }

  onProductionMode(event) {
    console.log('*** NewDocumentDlgProcessSpecComponent onProductionMode ' + JSON.stringify(event));

  }

  onNumberOfItemsToProduceChange(event) {
    console.log('*** NewDocumentDlgProcessSpecComponent onNumberOfItemsToProduceChange ' + JSON.stringify(event));

  }
}
